import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";

function EditarPeca() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // id da peça

  const [fornecedores, setFornecedores] = useState([]);
  const [maquinas, setMaquinas] = useState([]);

  const [nome, setNome] = useState("");
  const [fornecedorId, setFornecedorId] = useState("");
  const [maquinaId, setMaquinaId] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ondeSeAplica, setOndeSeAplica] = useState("");
  const [fotosTexto, setFotosTexto] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [carregando, setCarregando] = useState(true);
  const [carregandoListas, setCarregandoListas] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  useEffect(() => {
    async function carregarFornecedoresEMaquinas() {
      if (!usuarioLogado?.grupoId) return;
      setCarregandoListas(true);

      const grupoId = usuarioLogado.grupoId;
      const empresaId = usuarioLogado.empresaId || null;

      try {
        const fornecRef = collection(db, "fornecedores");
        let qF;
        if (empresaId) {
          qF = query(
            fornecRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qF = query(fornecRef, where("grupoId", "==", grupoId));
        }
        const snapF = await getDocs(qF);
        setFornecedores(
          snapF.docs.map((d) => ({ id: d.id, ...d.data() }))
        );

        const maqRef = collection(db, "maquinas");
        let qM;
        if (empresaId) {
          qM = query(
            maqRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qM = query(maqRef, where("grupoId", "==", grupoId));
        }
        const snapM = await getDocs(qM);
        setMaquinas(
          snapM.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (e) {
        console.error("Erro ao carregar listas:", e);
        setErro((prev) =>
          prev ? prev + " / Erro ao carregar fornecedores/máquinas." : "Erro ao carregar fornecedores/máquinas."
        );
      } finally {
        setCarregandoListas(false);
      }
    }

    carregarFornecedoresEMaquinas();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarPeca() {
      if (!id) return;

      try {
        setCarregando(true);
        const ref = doc(db, "pecas", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setErro("Peça não encontrada.");
          setCarregando(false);
          return;
        }

        const dados = snap.data();

        setNome(dados.nome || "");
        setFornecedorId(dados.fornecedorId || "");
        setMaquinaId(dados.maquinaId || "");
        setDescricao(dados.descricao || "");
        setOndeSeAplica(dados.ondeSeAplica || "");
        if (Array.isArray(dados.fotos)) {
          setFotosTexto(dados.fotos.join("\n"));
        } else if (typeof dados.fotos === "string") {
          setFotosTexto(dados.fotos);
        } else {
          setFotosTexto("");
        }
        setObservacoes(dados.observacoes || "");
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar peça.");
      } finally {
        setCarregando(false);
      }
    }

    carregarPeca();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!nome.trim()) {
      setErro("Informe o nome da peça.");
      return;
    }

    const fornecedorSelecionado = fornecedores.find((f) => f.id === fornecedorId);
    const fornecedorNome = fornecedorSelecionado?.nome || null;

    const maquinaSelecionada = maquinas.find((m) => m.id === maquinaId);
    const maquinaNome = maquinaSelecionada?.nome || null;

    const fotosArray = fotosTexto
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    try {
      setSalvando(true);

      const ref = doc(db, "pecas", id);
      await updateDoc(ref, {
        grupoId: usuarioLogado?.grupoId || null,
        grupoNome: usuarioLogado?.grupoNome || usuarioLogado?.grupo || null,
        nome: nome.trim(),
        fornecedorId: fornecedorId || null,
        fornecedorNome,
        maquinaId: maquinaId || null,
        maquinaNome,
        descricao: descricao.trim() || null,
        ondeSeAplica: ondeSeAplica.trim() || null,
        fotos: fotosArray.length ? fotosArray : [],
        observacoes: observacoes.trim() || null,
        updatedAt: serverTimestamp(),
      });

      setSucesso("Peça atualizada com sucesso!");
    } catch (e) {
      console.error(e);
      setErro("Erro ao salvar alterações.");
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <LayoutDesktop tema={tema} titulo="Editar Peça" subtitulo="">
        <p>Carregando dados da peça...</p>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Editar Peça"
      subtitulo={nome ? `Ajuste de dados da peça ${nome}` : ""}
    >
      <section className="pc-card">
        <h2 style={{ marginTop: 0 }}>Dados da peça</h2>

        {erro && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              background: "#7f1d1d",
              color: "#fecaca",
              fontSize: 14,
            }}
          >
            {erro}
          </div>
        )}

        {sucesso && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              background: "#14532d",
              color: "#bbf7d0",
              fontSize: 14,
            }}
          >
            {sucesso}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: 12,
            maxWidth: 720,
            marginLeft: "auto",
            marginRight: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* Nome */}
          <label>
            <span style={{ fontSize: 14 }}>Nome</span>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
              }}
            />
          </label>

          {/* Fornecedor */}
          <label>
            <span style={{ fontSize: 14 }}>Fornecedor</span>
            <select
              value={fornecedorId}
              onChange={(e) => setFornecedorId(e.target.value)}
              disabled={carregandoListas}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                color: "inherit",
              }}
            >
              <option value="">
                {carregandoListas
                  ? "Carregando fornecedores..."
                  : "Selecione o fornecedor"}
              </option>
              {fornecedores.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome || "(sem nome)"}
                </option>
              ))}
            </select>
          </label>

          {/* Máquina */}
          <label>
            <span style={{ fontSize: 14 }}>Máquina</span>
            <select
              value={maquinaId}
              onChange={(e) => setMaquinaId(e.target.value)}
              disabled={carregandoListas}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                color: "inherit",
              }}
            >
              <option value="">
                {carregandoListas
                  ? "Carregando máquinas..."
                  : "Selecione a máquina (opcional)"}
              </option>
              {maquinas.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome || "(sem nome)"}
                </option>
              ))}
            </select>
          </label>

          {/* Descrição */}
          <label>
            <span style={{ fontSize: 14 }}>Descrição</span>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          {/* Onde se aplica */}
          <label>
            <span style={{ fontSize: 14 }}>Onde se aplica</span>
            <textarea
              value={ondeSeAplica}
              onChange={(e) => setOndeSeAplica(e.target.value)}
              rows={2}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          {/* Fotos */}
          <label>
            <span style={{ fontSize: 14 }}>
              Fotos (URLs, uma por linha)
            </span>
            <textarea
              value={fotosTexto}
              onChange={(e) => setFotosTexto(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          {/* Observações */}
          <label>
            <span style={{ fontSize: 14 }}>Observações</span>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/cadastros/pecas")}
              className="pc-btn-secundario"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="pc-btn-primario"
              disabled={salvando}
            >
              {salvando ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </form>
      </section>
    </LayoutDesktop>
  );
}

export default EditarPeca;
